import { useEffect, useMemo, useRef, useState } from 'react'

import { MEDICAL_PLAN_PAGINATION } from '../constants'
import { isDefaultValue } from '../filterConversions'

export interface MedicalPlanFilters {
  state?: string
  year?: string
  zipCode?: string
  fipsCountyCode?: string
  memberId?: string
  hiosId?: string
  medicalPlanId?: string
  offMarketOnly?: boolean
  activelyMarketedOnly?: boolean
  carrierNames?: string[]
  metalTiers?: string[]
  planTypes?: string[]
  hsaEligible?: boolean
  page?: number
  perPage?: number
}

function toSnakeCase (str: string): string {
  return str.replace(/([A-Z])/g, '_$1').toLowerCase()
}

function getSearchParams (): URLSearchParams {
  return new URLSearchParams(window.location.search)
}

function getFilter (key: string): string | undefined {
  const value = getSearchParams().get(`filter[${key}]`)
  // Preserve empty strings
  // Return undefined only if the param doesn't exist (null)
  return value === null ? undefined : value
}

function getArrayFilter (key: string): string[] | undefined {
  const params = getSearchParams()
  const values: string[] = []
  params.forEach((value, paramKey) => {
    if (paramKey === `filter[${key}][]`) {
      // Only include non-empty values
      if (value !== '') {
        values.push(value)
      }
    }
  })
  // Only return array if it has values (non-default)
  if (values.length > 0) {
    return values
  }
  return undefined
}

function getBooleanFilter (key: string): boolean | undefined {
  const value = getSearchParams().get(`filter[${key}]`)
  return value === 'true' ? true : value === 'false' ? false : undefined
}

export function getDefaultYear (): string {
  const now = new Date()
  const currentYear = now.getFullYear()
  const month = now.getMonth() + 1 // getMonth() returns 0-11
  // Return the current year typically, but if it's December return the next year
  return month === 12 ? String(currentYear + 1) : String(currentYear)
}

export function useMedicalPlanFilters () {
  const [urlSearch, setUrlSearch] = useState(window.location.search)
  const urlSearchRef = useRef(window.location.search)

  // Listen for browser navigation (back/forward) to update URL state
  useEffect(() => {
    const handlePopState = () => {
      const currentSearch = window.location.search
      urlSearchRef.current = currentSearch
      setUrlSearch(currentSearch)
    }
    window.addEventListener('popstate', handlePopState)
    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [])

  // Initialize year in URL on first load (only if no filters are set)
  useEffect(() => {
    const params = getSearchParams()
    // Only set year if there are no filter params at all (initial page load)
    const hasAnyFilters = Array.from(params.keys()).some(key => key.startsWith('filter['))

    if (!hasAnyFilters) {
      const pageURL = new URL(window.location.href)
      // Always include year in URL
      pageURL.searchParams.set('filter[year]', getDefaultYear())
      // Don't set default boolean filters - they default to true but only appear in URL if set to false
      window.history.replaceState({}, '', pageURL.toString())
      setUrlSearch(pageURL.search)
    }
  }, []) // Only run on mount

  const filters: MedicalPlanFilters = useMemo(() => {
    const params = getSearchParams()

    return {
      state: getFilter('state'),
      year: getFilter('year') || getDefaultYear(),
      zipCode: getFilter('zip_code'),
      fipsCountyCode: getFilter('fips_county_code'),
      memberId: getFilter('member_id'),
      hiosId: getFilter('hios_id'),
      medicalPlanId: getFilter('medical_plan_id'),
      // Default to true if not explicitly set in URL
      offMarketOnly: getBooleanFilter('off_market_only') ?? true,
      activelyMarketedOnly: getBooleanFilter('actively_marketed_only') ?? true,
      carrierNames: getArrayFilter('carrier_names'),
      metalTiers: getArrayFilter('metal_tiers'),
      planTypes: getArrayFilter('plan_types'),
      // hsaEligible: only return boolean if it's explicitly set in URL
      hsaEligible: getBooleanFilter('hsa_eligible'),
      page: parseInt(params.get('page[number]') || String(MEDICAL_PLAN_PAGINATION.DEFAULT_PAGE_NUMBER)),
      perPage: parseInt(params.get('page[size]') || String(MEDICAL_PLAN_PAGINATION.DEFAULT_PAGE_SIZE)),
    }
  }, [urlSearch])

  const updateFilters = (updates: Partial<MedicalPlanFilters>) => {
    const pageURL = new URL(window.location.href)

    Object.entries(updates).forEach(([key, value]) => {
      const snakeKey = toSnakeCase(key)

      if (key === 'page') {
        pageURL.searchParams.set('page[number]', String(value || MEDICAL_PLAN_PAGINATION.DEFAULT_PAGE_NUMBER))
      } else if (key === 'perPage') {
        pageURL.searchParams.set('page[size]', String(value || MEDICAL_PLAN_PAGINATION.DEFAULT_PAGE_SIZE))
      } else if (key === 'year') {
        // Always include year in URL, even if it's the default
        pageURL.searchParams.set(`filter[${snakeKey}]`, String(value || getDefaultYear()))
      } else if (Array.isArray(value)) {
        // Remove existing array values
        pageURL.searchParams.delete(`filter[${snakeKey}][]`)
        // Only add to URL if array has values (non-default)
        if (value.length > 0) {
          // Add new array values
          value.forEach((v) => {
            pageURL.searchParams.append(`filter[${snakeKey}][]`, String(v))
          })
        }
        // Don't add empty arrays to URL - they're managed by visibility state
      } else if (value === undefined || value === null) {
        // Remove from URL when value is undefined/null
        pageURL.searchParams.delete(`filter[${snakeKey}]`)
        pageURL.searchParams.delete(`filter[${snakeKey}][]`)
      } else if (value === '') {
        // Don't add empty strings to URL - they're managed by visibility state
        pageURL.searchParams.delete(`filter[${snakeKey}]`)
        pageURL.searchParams.delete(`filter[${snakeKey}][]`)
      } else {
        // Only add non-default values to URL
        if (!isDefaultValue(key, value)) {
          pageURL.searchParams.set(`filter[${snakeKey}]`, String(value))
        } else {
          // Remove default values from URL
          pageURL.searchParams.delete(`filter[${snakeKey}]`)
          pageURL.searchParams.delete(`filter[${snakeKey}][]`)
        }
      }
    })

    // Ensure year is always in URL (in case it wasn't in updates)
    if (!('year' in updates)) {
      const currentYear = filters.year || getDefaultYear()
      pageURL.searchParams.set('filter[year]', currentYear)
    }

    // Reset to page 1 when filters change (except when updating page itself)
    if (!('page' in updates) && Object.keys(updates).length > 0) {
      pageURL.searchParams.set('page[number]', String(MEDICAL_PLAN_PAGINATION.DEFAULT_PAGE_NUMBER))
    }

    // Update URL without reload
    if (!window.history.state) {
      window.history.replaceState({}, '', pageURL.toString())
    } else {
      window.history.pushState({}, '', pageURL.toString())
    }
    setUrlSearch(pageURL.search)
  }

  const clearFilters = () => {
    const pageURL = new URL(window.location.href)
    pageURL.search = ''
    // Set default year after clearing
    pageURL.searchParams.set('filter[year]', getDefaultYear())
    window.history.replaceState({}, '', pageURL.toString())
    setUrlSearch(pageURL.search)
  }

  return { filters, updateFilters, clearFilters }
}
